import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useQuery } from 'react-apollo';
import { useLocation } from 'react-router-dom';
import pluralize from 'pluralize';

import useAuthenticated from '../../../../../../../../../../hooks/useAuthenticated';
import { MeForAlbumFoldersSelect } from '../../../../../../../../../../graphql/queries/account';
import { AlbumFolderForAlbumSelect } from '../../../../../../../../../../graphql/queries/album-folder';
import MultiSelect from '../../../../../../../../../../components/global/MultiSelect';
import { ALBUM_FOLDER_JOIN_TYPE } from '../../../../../../../../../../constants/album-folder';
import { getBasenameDefinitions } from '../../../../../../../../../../util/role-definitions';
import useBasename from '../../../../../../../../../../components/routing/useBasename';
import { getAlbumFolderAlbumFlatList } from '../../../../../../../../../../util/album-folder';

const propTypes = {
  contactLabel: PropTypes.string,
  contactLinkConfiguration: PropTypes.shape({
    albumFolderId: PropTypes.string,
    albums: PropTypes.array,
  }).isRequired,
  setContactLinkConfiguration: PropTypes.func.isRequired,
};

const ContactLinkSelector = ({
  contactLabel = 'contact',
  contactLinkConfiguration,
  setContactLinkConfiguration,
}) => {
  const { albumFolderId, albums } = contactLinkConfiguration;
  const basename = useBasename();
  const location = useLocation();
  const authenticated = useAuthenticated();
  const definitions = getBasenameDefinitions(basename);
  const { data: { me } = {} } = useQuery(MeForAlbumFoldersSelect, {
    variables: {
      album_type_subtype_web_app_basename_filters:
        definitions.allowedContentTypes,
    },
    skip: !authenticated,
  });
  const { data: { albumFolder } = {} } = useQuery(AlbumFolderForAlbumSelect, {
    variables: {
      id: albumFolderId,
    },
    skip: !albumFolderId,
  });

  useEffect(() => {
    if (!albumFolderId || albumFolder?.id !== albumFolderId) {
      setContactLinkConfiguration({
        ...contactLinkConfiguration,
        albums: [],
      });
    }
  }, [albumFolderId]);

  if (!authenticated || !location?.pathname?.includes('/address-book')) {
    return null;
  }

  const albumOptions = useMemo(() => {
    if (!albumFolder) {
      return [];
    }

    return getAlbumFolderAlbumFlatList(albumFolder, true);
  }, [albumFolder]);

  const isIndv = albumFolder?.join_type === ALBUM_FOLDER_JOIN_TYPE.INDIVIDUAL;

  const classes = classNames({
    'contact-link-selectors': true,
    'include-albums-select': isIndv,
  });

  const contactLabelPlural = pluralize(contactLabel, 2);

  return (
    <div className="mt-1 mb-2">
      <h4 className="mb-1">Gallery Link Configuration</h4>
      <p>
        Tie your {contactLabelPlural} below to a gallery and keep them
        organized. Your {contactLabelPlural} will not be joined to the gallery
        nor appear on the {contactLabel} dashboard, but you will be able to
        filter them via the Relationship Filter bar on your address book.
      </p>
      <div className={classes}>
        <MultiSelect
          id="album-folder-select"
          name="albumFolderSelect"
          label="Select a gallery"
          data-testid="album-folder-select"
          placeholder="Select a gallery"
          value={
            albumFolder
              ? { value: albumFolder.id, label: albumFolder.name }
              : null
          }
          options={(me?.album_folders?.records ?? []).map(folder => ({
            label: folder.name,
            value: folder.id,
          }))}
          onChange={option => {
            setContactLinkConfiguration({
              ...contactLinkConfiguration,
              albumFolderId: option?.value,
            });
          }}
          isClearable
        />
        {isIndv && (
          <MultiSelect
            id="albums-select"
            name="albumsSelect"
            label="Select albums"
            data-testid="albums-select"
            placeholder={
              !albumFolderId || !albumFolder
                ? 'Select a gallery first...'
                : 'Select albums'
            }
            noOptionsMessage={() =>
              !albumFolderId || !albumFolder
                ? 'Select a gallery first...'
                : 'This gallery does not have any albums'
            }
            isDisabled={!albumFolderId || !albumFolder}
            value={albums.map(album => ({
              value: album.id,
              label: album.name,
            }))}
            options={albumOptions.map(album => ({
              label: album.name,
              value: album.id,
            }))}
            onChange={options => {
              setContactLinkConfiguration({
                ...contactLinkConfiguration,
                albums: options.map(opt => ({
                  id: opt.value,
                  name: opt.label,
                })),
              });
            }}
            isMulti
            isClearable
          />
        )}
      </div>
    </div>
  );
};

ContactLinkSelector.propTypes = propTypes;

export default ContactLinkSelector;
